// =============================================================================
// lib/auth-store.ts — Estado global de autenticação (Zustand).
//
// Segurança (LGPD + auth):
//   - access_token APENAS em memória (campo do store Zustand). NUNCA localStorage.
//   - Refresh via cookie httpOnly — frontend só faz POST /api/auth/refresh.
//   - user.email é PII — nunca logar no console.
//   - clear() é chamado em logout e em refresh falho (lib/api.ts) e também descarta o
//     rascunho em memória do e-mail pessoal (F58-S15).
//
// O store NÃO persiste via zustand/middleware/persist — intencional.
// Preferências de UI (tema, sidebar) persistem em ThemeProvider / sidebarStore.
// =============================================================================

import { create } from 'zustand';

import { clearPersonalEmailDraft } from '../features/account/personalEmail';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  organizationId: string;
  /** Permissões/roles — populado em futuras iterações (F1-S04+) */
  permissions: string[];
  /**
   * Escopo de cidade do usuário sincronizado no payload de auth:
   *   null     → admin/gestor_geral — acesso global (sem filtro de cidade).
   *   string[] → UUIDs das cidades permitidas (gestor_regional/agente).
   *   []       → sem cidade configurada.
   *
   * Usado para inferir opções do scope toggle em /relatorios sem heurística
   * de permissão. O backend NUNCA confia neste campo para autorização.
   */
  cityScopeIds: string[] | null;
}

/**
 * Estado do primeiro acesso (F56-S02) — só em memória, nunca em storage.
 *   'unknown' → ainda não sabemos (sessão restaurada por refresh; resolvido por GET /api/auth/me).
 *   'pending' → entrou com senha temporária: só a tela /primeiro-acesso é utilizável.
 *   'done'    → sem troca obrigatória pendente.
 *
 * É estado COSMÉTICO de roteamento. Quem barra de verdade é o servidor
 * (403 CREDENTIALS_CHANGE_REQUIRED em toda rota fora da allowlist).
 */
export type FirstAccessState = 'unknown' | 'pending' | 'done';

export interface AuthState {
  /** Access token em memória (nunca persiste em storage) */
  accessToken: string | null;
  /** Usuário autenticado */
  user: AuthUser | null;
  /** true quando accessToken != null e user != null */
  isAuthenticated: boolean;
  /** Primeiro acesso pendente? Ver `FirstAccessState`. Reseta para 'unknown' em clear(). */
  firstAccess: FirstAccessState;
  /**
   * Define user + token após login. `firstAccess` omitido = 'unknown' (sessão restaurada
   * por refresh: o guard resolve via GET /api/auth/me antes de abrir qualquer rota).
   */
  setAuth: (user: AuthUser, token: string, firstAccess?: FirstAccessState) => void;
  /** Atualiza apenas o access token (refresh interceptor) */
  setAccessToken: (token: string) => void;
  /** Atualiza só o estado do primeiro acesso (resposta de GET /api/auth/me, 403 do servidor) */
  setFirstAccess: (state: FirstAccessState) => void;
  /** Limpa toda sessão */
  clear: () => void;
  /** Verifica se o usuário possui uma permissão específica (RBAC). */
  hasPermission: (permission: string) => boolean;
}

// ─── Estado inicial ───────────────────────────────────────────────────────────

const INITIAL_DATA = {
  accessToken: null as string | null,
  user: null as AuthUser | null,
  isAuthenticated: false,
  firstAccess: 'unknown' as FirstAccessState,
};

// ─── Store ────────────────────────────────────────────────────────────────────

export const useAuthStore = create<AuthState>()((set, get) => ({
  ...INITIAL_DATA,

  setAuth(user: AuthUser, token: string, firstAccess: FirstAccessState = 'unknown') {
    set({ user, accessToken: token, isAuthenticated: true, firstAccess });
  },

  setFirstAccess(state: FirstAccessState) {
    set({ firstAccess: state });
  },

  setAccessToken(token: string) {
    set({ accessToken: token });
  },

  clear() {
    // LGPD: o rascunho do e-mail pessoal (dado pessoal, só em memória) não sobrevive à sessão.
    // personalEmail.ts é módulo puro, sem imports: não cria ciclo com o store.
    clearPersonalEmailDraft();
    set({ ...INITIAL_DATA });
  },

  hasPermission(permission: string) {
    const { user } = get();
    if (!user) return false;
    return user.permissions.includes(permission);
  },
}));

// ─── Hook conveniente ─────────────────────────────────────────────────────────

/**
 * Hook para consumo nos componentes.
 * Expõe o slice de auth + helpers semânticos.
 */
export function useAuth() {
  return useAuthStore((state) => ({
    user: state.user,
    accessToken: state.accessToken,
    isAuthenticated: state.isAuthenticated,
    hasPermission: state.hasPermission,
    setAuth: state.setAuth,
    clear: state.clear,
  }));
}
